import { useCallback, useEffect, useRef } from "react";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { IconButton } from "@astryxdesign/core/IconButton";
import { DropdownMenu } from "@astryxdesign/core/DropdownMenu";
import {
  ArrowDown,
  ArrowDownLeft,
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpLeft,
  ArrowUpRight,
  MapPin,
  Square,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import type { PTZPreset, PTZVector } from "../../lib/api";
import { directionVector, zoomVector, type PtzControl } from "./usePtzControl";

interface PtzPadProps {
  control: PtzControl;
  /** Positions stored on the camera itself, already filtered to the ones the
   *  recall endpoint will accept. Empty renders no preset control at all —
   *  a "Presets" button over an empty menu is a dead affordance, and on a
   *  camera with none configured that is exactly what it would be. */
  presets?: PTZPreset[];
  /** Disables every key — used while the stream isn't live, since aiming a
   *  camera you can't currently see is guesswork. */
  isDisabled?: boolean;
}

interface PadKey {
  label: string;
  icon: React.ReactNode;
  vector: PTZVector;
}

// pan +1 is right and tilt +1 is up, matching the backend's model.PTZVector.
// Laid out as three rows so the source order matches what an operator sees,
// which is what makes a mirrored sign obvious on reading rather than only
// once a camera swings the wrong way.
const TOP_ROW: PadKey[] = [
  { label: "Pan left and tilt up", icon: <ArrowUpLeft size={16} />, vector: directionVector(-1, 1) },
  { label: "Tilt up", icon: <ArrowUp size={16} />, vector: directionVector(0, 1) },
  { label: "Pan right and tilt up", icon: <ArrowUpRight size={16} />, vector: directionVector(1, 1) },
];
// Named rather than an array like the rows above, because the middle row is
// interrupted by the stop key and each side is placed individually — an
// array would only mean indexing it back out with a non-null assertion.
const PAN_LEFT: PadKey = { label: "Pan left", icon: <ArrowLeft size={16} />, vector: directionVector(-1, 0) };
const PAN_RIGHT: PadKey = { label: "Pan right", icon: <ArrowRight size={16} />, vector: directionVector(1, 0) };
const BOTTOM_ROW: PadKey[] = [
  { label: "Pan left and tilt down", icon: <ArrowDownLeft size={16} />, vector: directionVector(-1, -1) },
  { label: "Tilt down", icon: <ArrowDown size={16} />, vector: directionVector(0, -1) },
  { label: "Pan right and tilt down", icon: <ArrowDownRight size={16} />, vector: directionVector(1, -1) },
];
const ZOOM_ROW: PadKey[] = [
  { label: "Zoom out", icon: <ZoomOut size={16} />, vector: zoomVector(-1) },
  { label: "Zoom in", icon: <ZoomIn size={16} />, vector: zoomVector(1) },
];

/** One press-and-hold key.
 *
 *  The pointer and keyboard handlers sit on a wrapper rather than on the
 *  IconButton because IconButton exposes only onClick — and a PTZ key is not
 *  a click, it is a hold. Both kinds of event bubble from the button to this
 *  wrapper, so the button keeps all of its own styling, focus ring and
 *  accessible label while the wrapper reads the gesture.
 */
function PadButton({ item, control, isDisabled }: { item: PadKey; control: PtzControl; isDisabled?: boolean }) {
  const isHeldRef = useRef(false);

  // The control is read through a ref rather than closed over directly.
  //
  // usePtzControl returns a fresh object on every render — isMoving and error
  // are state, so its identity necessarily changes the moment a press starts.
  // If `release` depended on that object, the window-listener effect below
  // would tear down and re-register on that very first re-render, and its
  // cleanup would call release() on a press the operator is still holding.
  // The symptom is subtle and specific: the move starts, stops itself a frame
  // later, and the keep-alive never runs — a held button that moves the
  // camera for an instant and then quietly gives up.
  const controlRef = useRef(control);
  controlRef.current = control;

  const release = useCallback(() => {
    if (!isHeldRef.current) return;
    isHeldRef.current = false;
    controlRef.current.endMove();
  }, []);

  const press = useCallback(() => {
    if (isDisabled || isHeldRef.current) return;
    isHeldRef.current = true;
    controlRef.current.startMove(item.vector);
  }, [isDisabled, item.vector]);

  // Released on window, not on the button: a pointer that goes down here and
  // comes up somewhere else — dragged off the pad, off the video, out of the
  // window entirely — still has to stop the camera. Listening only on the
  // button would leave the mount turning in exactly that case.
  //
  // `release` is stable (see above), so this registers once per mount and its
  // cleanup runs only on a real unmount.
  useEffect(() => {
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      release();
    };
  }, [release]);

  return (
    <HStack
      onPointerDown={press}
      // Space and Enter are how a keyboard user holds a button. `repeat`
      // guards against the OS key-repeat firing press again every 30ms once
      // the key is held — press is idempotent, but the request it sends is
      // not free.
      onKeyDown={(e) => {
        if (e.repeat) return;
        if (e.key === " " || e.key === "Enter") press();
      }}
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Enter") release();
      }}
      onBlur={release}
    >
      <IconButton label={item.label} tooltip={item.label} icon={item.icon} variant="secondary" size="sm" isDisabled={isDisabled} />
    </HStack>
  );
}

/** An eight-way direction pad plus zoom, for a camera with a motorised mount.
 *
 *  Keys are press-and-hold: the mount moves while held and stops on release.
 *  The release is not the only thing that stops it — see usePtzControl and
 *  the backend's watchdog for why that guarantee cannot rest on a single
 *  network request arriving.
 */
export function PtzPad({ control, presets = [], isDisabled }: PtzPadProps) {
  const renderRow = (items: PadKey[]) => (
    <HStack gap={1} justify="center">
      {items.map((item) => (
        <PadButton key={item.label} item={item} control={control} isDisabled={isDisabled} />
      ))}
    </HStack>
  );

  return (
    <VStack gap={1} hAlign="center">
      {renderRow(TOP_ROW)}
      <HStack gap={1} justify="center" vAlign="center">
        <PadButton item={PAN_LEFT} control={control} isDisabled={isDisabled} />
        {/* An explicit stop in the middle of the pad. The watchdog already
            stops an abandoned move within a couple of seconds, so this is
            not what makes the camera safe — it is the control an operator
            reaches for when something looks wrong, and a pad with a dead
            centre invites clicking there anyway. */}
        <IconButton
          label="Stop moving"
          tooltip="Stop"
          icon={<Square size={14} />}
          variant="ghost"
          size="sm"
          isDisabled={isDisabled}
          onClick={control.endMove}
        />
        <PadButton item={PAN_RIGHT} control={control} isDisabled={isDisabled} />
      </HStack>
      {renderRow(BOTTOM_ROW)}
      {renderRow(ZOOM_ROW)}

      {/* Only when the camera actually has positions stored. The list comes
          from the device, so these are the operator's own names ("Haul road
          gate"), not numbers to guess at — and a camera with none shows no
          control rather than a menu with nothing in it.

          This is what makes a driveable camera recoverable: once someone has
          panned a dome away from what it was installed to watch, a preset is
          the only way back short of walking to it. */}
      {presets.length > 0 && (
        <DropdownMenu
          button={{
            label: "Presets",
            icon: <MapPin size={14} />,
            variant: "secondary",
            size: "sm",
            isDisabled,
          }}
          // Below, not above: the pad is anchored to the top of the frame
          // (see ptzOverlayStyle), so a menu opening upward would cover the
          // direction keys it sits under.
          placement="below"
          alignment="end"
          items={presets.map((preset) => ({
            id: String(preset.id),
            // Devices assign default names, so this is rarely empty — but
            // nothing guarantees it, and a blank row would be unclickable in
            // practice.
            label: preset.name || `Preset ${preset.id}`,
            onClick: () => control.gotoPreset(preset.id),
          }))}
        />
      )}

      {control.error && (
        <Text type="supporting" color="inherit" justify="center" maxLines={2}>
          {control.error}
        </Text>
      )}
    </VStack>
  );
}
